import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

// Business / job-site timezone. Timesheet times always represent the wall
// clock at the work site, so they are entered and displayed in this zone
// regardless of where the viewer's browser happens to be.
export const BUSINESS_TZ = 'Australia/Perth'

// Short timezone label (e.g. "AEST", "GMT+11") for `tz`, shown only when it
// differs from the viewer's own device timezone — Deputy's pattern (see
// docs/designs/per-site-timezones.md Phase 2.3, premise 5: "Everyone sees
// site time. The timezone abbreviation shows only when it differs from the
// viewer's device"). Returns null when `tz` is falsy, matches the browser's
// zone, or Intl can't resolve it (an invalid/unrecognised IANA name).
export function timezoneLabel(tz) {
  if (!tz) return null
  let browserTz
  try {
    browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (e) {
    return null
  }
  if (tz === browserTz) return null
  try {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(new Date())
    const tzPart = parts.find((p) => p.type === 'timeZoneName')
    return tzPart ? tzPart.value : null
  } catch (e) {
    return null
  }
}

// Parse an API datetime (UTC instant) into a dayjs in the given timezone.
// `tz` defaults to BUSINESS_TZ so existing callers keep working unchanged
// until they're swept to pass a real per-event/per-project zone (see
// docs/designs/per-site-timezones.md Phase 2.2).
export function bizFromIso(iso, tz = BUSINESS_TZ) {
  return dayjs.utc(iso).tz(tz)
}

// Current moment expressed in the given timezone (defaults to BUSINESS_TZ).
export function bizNow(tz = BUSINESS_TZ) {
  return dayjs().tz(tz)
}

// Format an API datetime as a time-of-day in the given timezone.
export function formatBusinessTime(iso, fmt = 'h:mm a', tz = BUSINESS_TZ) {
  if (!iso) return ''
  return bizFromIso(iso, tz).format(fmt)
}

// Format an API datetime as a full date-time in the given timezone.
export function formatBusinessDateTime(
  iso,
  fmt = 'D MMM YYYY, h:mm a',
  tz = BUSINESS_TZ,
) {
  if (!iso) return ''
  return bizFromIso(iso, tz).format(fmt)
}

// Combine a picked date + time (interpreted as wall clock in `tz`) into a
// UTC ISO string to send to the API.
export function combineBusinessDateTime(date, time, tz = BUSINESS_TZ) {
  if (!date) return null
  return dayjs.tz(date + 'T' + (time || '00:00'), tz).toISOString()
}

// Split an API datetime (UTC instant) into date + time parts in `tz`, for
// prefilling form inputs.
export function splitBusinessIso(iso, tz = BUSINESS_TZ) {
  if (!iso) return { date: '', time: '' }
  const d = bizFromIso(iso, tz)
  return { date: d.format('YYYY-MM-DD'), time: d.format('HH:mm') }
}

export function localDateTimeToIso(date, time) {
  if (!date || !time) return null
  const normalizedTime = time.length === 5 ? time + ':00' : time
  return dayjs(date + 'T' + normalizedTime).format()
}

export function zoneDisplayName(zone) {
  if (!zone) return '-'
  return zone.name || zone.zone_name || '-'
}

export function sameProjectId(a, b) {
  if (a == null || b == null || a === '' || b === '') return false
  return String(a) === String(b)
}

// Some legacy/seeded records store `trade` as a JSON-encoded array (e.g.
// '["Apprentice","Welder"]') instead of the usual comma-joined string —
// detect and flatten that case so it doesn't render with brackets/quotes.
export function formatTrade(trade) {
  if (!trade) return ''
  if (typeof trade === 'string' && trade.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(trade)
      if (Array.isArray(parsed)) return parsed.join(', ')
    } catch (e) {
      // not valid JSON — fall through and show as-is
    }
  }
  return trade
}

// Human label for a plant/equipment rate_type value.
export function equipmentRateTypeLabel(rateType) {
  return rateType === 'day' ? 'Day' : 'Hourly'
}

// "1.5 hrs" / "1 day" — qty plus unit for a timesheet plant entry, so the
// crew-level view can show whether the qty is hourly or daily without
// revealing the $ rate (see SupervisorCrewPlantPanel/Cards showCost).
export function plantEntryQtyLabel(pe) {
  const qty = pe.quantity
  if (pe.rate_type_snapshot === 'hourly') {
    return `${qty} hr${qty === 1 ? '' : 's'}`
  }
  return `${qty} day${qty === 1 ? '' : 's'}`
}

// The API refuses a plant quantity above this.
export const PLANT_QUANTITY_MAX = 999.99

// Keeps a typed plant quantity inside [min, PLANT_QUANTITY_MAX] to two decimal
// places. Blank or non-numeric becomes `min`, or null when there is no minimum.
export function clampPlantQuantity(value, min = 0) {
  const n = Number(value)
  if (value === null || value === '' || Number.isNaN(n)) {
    return min > 0 ? min : null
  }
  return Math.round(Math.min(Math.max(n, min), PLANT_QUANTITY_MAX) * 100) / 100
}

// A readable message when the API rejected a plant entry, else null. Without
// this the tradie sees the raw "plant_entries.0.quantity may not be greater..."
export function plantEntryErrorMessage(responseData) {
  const errors = (responseData && responseData.errors) || {}
  const key = Object.keys(errors).find((k) => k.startsWith('plant_entries'))
  if (!key) return null
  return key.endsWith('quantity')
    ? `Equipment amount must be between 1 and ${PLANT_QUANTITY_MAX}.`
    : 'Check the equipment you added and try again.'
}

// Build the {value, text} option list for a plant-equipment <b-form-select>,
// including a leading "Select equipment" placeholder. `items` are raw
// plantEquipment/items records (id, name, plant_id_rego, rate_type).
export function buildEquipmentOptions(items) {
  const options = (items || []).map((item) => {
    const namePart = item.plant_id_rego
      ? `${item.name} (${item.plant_id_rego})`
      : item.name
    return {
      value: item.id,
      text: `${namePart} - ${equipmentRateTypeLabel(item.rate_type)}`,
      rateType: item.rate_type,
    }
  })
  return [{ value: null, text: 'Select equipment' }, ...options]
}

// Split a list into two roughly-even columns for a two-column checkbox
// layout, first column getting the extra item on an odd count.
export function splitIntoColumns(list) {
  const half = Math.ceil(list.length / 2)
  return [list.slice(0, half), list.slice(half)]
}

export function projectNameFromEvent(event, projects) {
  // Events carry a top-level `project` sourced from the event's own
  // project_id (authoritative). The nested zone.project only reflects the
  // zone matched at clock-in, so it's a legacy fallback.
  if (event.project && event.project.name) {
    return event.project.name
  }
  if (event.zone && event.zone.project && event.zone.project.name) {
    return event.zone.project.name
  }
  const projectId = event.project_id
  if (projectId && projects && projects.length) {
    const match = projects.find(function (p) {
      return sameProjectId(p.id, projectId)
    })
    if (match && match.name) return match.name
  }
  return 'Unassigned Project'
}

export function isOnSite(event) {
  return event && event.status === 'clocked_in' && event.within_zone_in === true
}

// Unlike isOnSite(), this doesn't require the worker to still be clocked in —
// it's for checking historical days, where the clock-in event's status has
// since moved on to clocked_out/approved but the geofence check at the time
// of clock-in is still a valid record of attendance.
export function wasZoneVerified(event) {
  return !!(event && event.within_zone_in === true)
}

// within_zone_in is tri-state: true = GPS inside a project zone,
// false = GPS outside all project zones, null/undefined = no check
// applicable (no zones on the project, no GPS, or a manual entry).
export function zoneCheckState(event) {
  if (!event || event.within_zone_in == null) return 'none'
  return event.within_zone_in === true ? 'in' : 'out'
}

export function zoneCheckLabel(event) {
  const state = zoneCheckState(event)
  if (state === 'in') return 'In zone'
  if (state === 'out') return 'Out of zone'
  return 'Location unknown'
}

export function isClockedIn(event) {
  return event && event.status === 'clocked_in'
}

// Blank per-tradie aggregate row for the tradie-list views (both the
// single-project and all-projects groupings build these before folding
// events onto them).
export function emptyTradieRow(id, name, trade = '') {
  return {
    tradieId: id,
    tradieName: name,
    trade: formatTrade(trade),
    eventsByDay: {},
    totalEntries: 0,
    totalHours: 0,
    approvedHours: 0,
    pendingCount: 0,
    activeEvent: null,
  }
}

// Fold one timesheet event onto a tradie-list aggregate row (mutates `tm`,
// built by emptyTradieRow). Shared by groupedByProject and projectGroups —
// a tradie can have a mix of GPS events and manual/supervisor entries, all
// merging into the one row.
export function applyEventToTradieRow(tm, e, tz = BUSINESS_TZ) {
  if (!tm.trade && e.tradie && e.tradie.trade) {
    tm.trade = formatTrade(e.tradie.trade)
  }
  // Prefer the API's own site_date (the row's stamped timezone, computed
  // once server-side — see docs/designs/per-site-timezones.md) over
  // recomputing it here. Falls back to a client-side calc using `tz`/
  // event.timezone for an event that predates the site_date column, or a
  // synthetic leave-day entry (never has clocked_in_at at all — see the
  // leaveDayInstances caller).
  const dayKey =
    e.site_date ||
    (e.clocked_in_at
      ? bizFromIso(e.clocked_in_at, e.timezone || tz).format('YYYY-MM-DD')
      : null)
  if (dayKey) {
    if (!tm.eventsByDay[dayKey]) tm.eventsByDay[dayKey] = []
    tm.eventsByDay[dayKey].push(e)
  }
  tm.totalEntries += 1
  if (e.status === 'clocked_in' && !tm.activeEvent) tm.activeEvent = e
  if (e.status === 'clocked_out') tm.pendingCount += 1
  if (e.clocked_in_at && e.clocked_out_at) {
    // payroll_minutes (API-computed, App\Support\TimesheetEventHours) is
    // already zero for a scheduled/pending_confirmation entry regardless of
    // its raw clocked_in_at/clocked_out_at — using it here, not re-diffing
    // the raw timestamps, is what keeps an unconfirmed future entry from
    // inflating this total. Falls back to the raw diff only against an API
    // response that predates this field. See
    // docs/designs/future-date-timesheet-entries.md.
    const mins =
      e.payroll_minutes !== undefined && e.payroll_minutes !== null
        ? e.payroll_minutes
        : (new Date(e.clocked_out_at) - new Date(e.clocked_in_at)) / 60000 -
          (e.break_minutes || 0)
    tm.totalHours = Math.round(tm.totalHours * 10 + (mins / 60) * 10) / 10
    if (e.status === 'approved')
      tm.approvedHours =
        Math.round(tm.approvedHours * 10 + (mins / 60) * 10) / 10
  }
}

// Workers visible and editable on a supervisor timesheet card.
//
// The rule is has_token OR grants_free_access, not has_token alone. A company
// on the free tier (or inside its 30-day trial) gets a row seeded for every
// project tradie server-side — see _eligibleRowUserIds in the API — so
// filtering on has_token alone throws away rows the API deliberately created
// and returned. That is what hid supervisor-invited workers from the BA while
// the supervisor could still see them in the app, which reads the same pair
// (weekly_grid.dart: hasToken || grantsFreeAccess).
//
// grants_free_access is per-row rather than per-sheet because that is the
// shape the supervisor endpoint already sends; undefined is falsy, so this
// degrades to the old behaviour against an API that doesn't send it yet.
export function tokenRows(ts) {
  return ts.rows.filter(
    (r) => r.worker.has_token || r.worker.grants_free_access,
  )
}

export function supervisorStatusVariant(status) {
  return (
    {
      submitted: 'warning',
      approved: 'success',
      rejected: 'danger',
      draft: 'secondary',
    }[status] || 'secondary'
  )
}

export function auditActionVariant(action) {
  return (
    {
      submitted: 'warning',
      approved: 'success',
      rejected: 'danger',
      resubmitted: 'info',
    }[action] || 'secondary'
  )
}

// "12 Aug – 18 Aug" for a supervisor timesheet's week_start.
export function formatWeekRange(weekStart) {
  const base = new Date(weekStart + 'T00:00:00')
  const end = new Date(base)
  end.setDate(base.getDate() + 6)
  const fmt = (d) =>
    d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
  return `${fmt(base)} – ${fmt(end)}`
}

// "Fri 25 Sep" - when a supervisor sheet was submitted. '' when it hasn't been.
export function formatSubmittedOn(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

// "1 day late" / "5 days late". '' when on time or not submitted. The count
// itself comes from the API (days_late), worked out in the sheet's timezone.
export function formatDaysLate(daysLate) {
  if (!daysLate || daysLate <= 0) return ''
  return daysLate === 1 ? '1 day late' : `${daysLate} days late`
}

export function formatAuditDateTime(iso) {
  if (!iso) return ''
  const formatted = new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
  // Non-breaking space between the time and am/pm so they never wrap apart
  // (e.g. "am" left dangling alone on its own line).
  return formatted.replace(/ (am|pm)$/i, ' $1')
}

// Events on a tradie-list row that are ready for an "Approve All" action.
// Delegates to isApprovableEvent (below) so this stays in sync with what the
// per-row Approve button treats as approvable — this used to check only
// status === 'clocked_out', which silently dropped passed "scheduled" entries
// (case 2 below) from Approve All even though they show an Approve button
// individually, and hid the Approve All button entirely for a row whose only
// pending work was that kind of entry.
export function getApprovableEvents(row) {
  return Object.values(row.eventsByDay)
    .reduce(function (acc, dayEvents) {
      return acc.concat(dayEvents)
    }, [])
    .filter(isApprovableEvent)
}

// confirmation_status (scheduled/pending_confirmation) is a separate concept
// from `status` (clocked_in/clocked_out/approved/rejected) — see the API's
// TimesheetZoneEvent::getConfirmationStatusAttribute() and
// docs/designs/future-date-timesheet-entries.md. Checked first here since a
// future-dated or aged-unconfirmed entry needs to read as that, not as
// whatever its underlying clock status happens to be.
export function eventStatusLabel(event) {
  if (!event) return ''
  if (event.confirmation_status === 'scheduled') return 'Scheduled'
  if (event.confirmation_status === 'pending_confirmation')
    return 'Pending confirmation'
  if (!event.status) return ''
  if (event.status === 'clocked_in') {
    return isOnSite(event) ? 'On site' : 'Clocked in'
  }
  return event.status.replace(/_/g, ' ')
}

// An entry a supervisor/BA can approve or reject from a dashboard/list view.
// Two cases qualify:
//   1. a normally clocked-out shift (status === 'clocked_out'), and
//   2. a pre-entered ("scheduled") entry whose date has passed — it reads as
//      pending_confirmation and carries a planned start + finish but never an
//      actual clock-out event, so its status stays clocked_in/scheduled.
// For case 2 the approve/reject handlers fold in the confirmation step (see
// confirmIfPending in the dashboard/history tabs), so it's still one click.
// A still-open shift with no clock-out time yet has nothing to approve.
export function isApprovableEvent(event) {
  if (!event) return false
  if (event.status === 'clocked_out') return true
  return (
    event.confirmation_status === 'pending_confirmation' &&
    !!event.clocked_in_at &&
    !!event.clocked_out_at
  )
}

export function eventStatusClass(event) {
  if (!event) return 'status-unknown'
  if (event.confirmation_status === 'scheduled') return 'status-scheduled'
  if (event.confirmation_status === 'pending_confirmation')
    return 'status-pending_confirmation'
  if (!event.status) return 'status-unknown'
  if (isOnSite(event)) return 'status-on_site'
  return 'status-' + event.status
}

// Night-shift window for the tradie timesheet list, where an entry is a
// single clock-in/out pair rather than a scheduled start/finish (so the
// crosses-midnight isOvernight() below doesn't apply here). "Night" is
// judged purely off the clock-in hour, in the business timezone, so an
// event still shows as night even before it's clocked out.
const NIGHT_START_HOUR = 18 // 6pm
const NIGHT_END_HOUR = 6 // 6am

export function isNightClockIn(clockedInAt, tz = BUSINESS_TZ) {
  if (!clockedInAt) return false
  const hour = bizFromIso(clockedInAt, tz).hour()
  return hour >= NIGHT_START_HOUR || hour < NIGHT_END_HOUR
}

// How many calendar days past its start day a clock event ends, read in the
// event's own timezone (0 = ends the same day, 1 = the next day, and so on).
// The week grid shows times of day only, so without this a shift that runs
// from Thursday afternoon to Friday evening reads as a five-hour shift.
export function shiftEndDayOffset(event) {
  if (!event || !event.clocked_in_at || !event.clocked_out_at) return 0
  const tz = event.timezone || BUSINESS_TZ
  const start = bizFromIso(event.clocked_in_at, tz).startOf('day')
  const end = bizFromIso(event.clocked_out_at, tz).startOf('day')
  return Math.max(0, end.diff(start, 'day'))
}

// "Fri 30 Oct" for a shift that ends after the day it started, else ''.
export function shiftEndLabel(event) {
  if (shiftEndDayOffset(event) === 0) return ''
  const tz = event.timezone || BUSINESS_TZ
  return bizFromIso(event.clocked_out_at, tz).format('ddd D MMM')
}

// Clocked time minus break, from the raw timestamps. Not payroll minutes: an
// unconfirmed shift counts as 0 payroll hours, but a 29-hour span is still
// worth a second look before anyone confirms it.
export function eventSpanMinutes(event) {
  if (!event || !event.clocked_in_at || !event.clocked_out_at) return 0
  const mins =
    (new Date(event.clocked_out_at) - new Date(event.clocked_in_at)) / 60000 -
    (event.break_minutes || 0)
  return Math.max(0, Math.round(mins))
}

// A single shift longer than a day may cover (MAX_SHIFT_MINUTES, 16h, the
// same limit the API puts on a supervisor timesheet day). Almost always a
// forgotten clock-out or a mistyped time.
export function isLongShift(event) {
  if (!event || event.is_leave) return false
  return eventSpanMinutes(event) > MAX_SHIFT_MINUTES
}

// ── Supervisor timesheet hours ────────────────────────────────────────────
//
// A supervisor timesheet day stores a calendar date plus two 'HH:mm' strings
// and nothing else, so a shift running past midnight is inferred: a finish
// earlier than the start means it ends the next morning. This mirrors
// SupervisorTimesheetDay::computeWorkedMinutes() in the API and
// TimesheetDayCell.workedDuration in the Flutter app — the three must agree,
// and tests/Unit/Models/SupervisorTimesheetDayTest.php holds the case table.

// Longest span a single day may cover. Matches MAX_SHIFT_MINUTES in the API.
export const MAX_SHIFT_MINUTES = 960 // 16h

// Minutes since midnight, or null when the value can't be read.
//
// The column is a bare varchar(5) with no padding constraint, so legacy rows
// may hold '8:00' rather than '08:00'. Comparing those as strings is actively
// wrong — '22:00' < '8:00' is true, which would read an ordinary 8am-10pm day
// as an overnight one. Everything here compares minutes, never strings.
export function timeToMinutes(time) {
  if (time === null || time === undefined || String(time).trim() === '')
    return null
  const m = /^\s*(\d{1,2}):([0-5]\d)/.exec(String(time))
  if (!m) return null
  const hour = Number(m[1])
  if (hour > 23) return null
  return hour * 60 + Number(m[2])
}

// Strictly less-than: an identical start and finish is a zero-length day,
// not a 24-hour one.
export function isOvernight(start, finish) {
  const s = timeToMinutes(start)
  const f = timeToMinutes(finish)
  if (s === null || f === null) return false
  return f < s
}

// Night shift for a supervisor timesheet day. Same 18:00-06:00 window as
// isNightClockIn() above, but read off the declared 'HH:mm' start rather than
// a clock-in timestamp, so the supervisor table and the tradie list agree on
// what counts as a night. A shift is night if it starts in the window OR
// (when a finish is given) crosses midnight: 19:52-21:52 is a night that never
// crosses midnight, 15:00-08:00 is a night that starts in daylight.
export function isNightStart(start, finish = null) {
  const s = timeToMinutes(start)
  if (s === null) return false
  return (
    s >= NIGHT_START_HOUR * 60 ||
    s < NIGHT_END_HOUR * 60 ||
    isOvernight(start, finish)
  )
}

// Gross span in minutes, adding a day when the shift runs past midnight.
export function spanMinutes(start, finish) {
  const s = timeToMinutes(start)
  let f = timeToMinutes(finish)
  if (s === null || f === null) return 0
  if (f < s) f += 1440
  return f - s
}

// Net worked minutes for a supervisor timesheet day, floored at zero. With
// overnight handled, zero is now only reachable via a break longer than the
// span — which the API rejects on write, so it only affects legacy rows.
//
// break_minutes is floored at zero too: the API rejects a negative break on
// write, but a negative value can still exist transiently while a supervisor
// is mid-keystroke typing "-30", or on a legacy/imported row that predates
// validation. Left unclamped, a negative break inflates the span instead of
// reducing it (span - (-30) = span + 30), showing more hours worked than the
// shift actually covers.
export function dayMinutes(day) {
  if (!day || !day.present || !day.start || !day.finish) return 0
  const span = spanMinutes(day.start, day.finish)
  if (span <= 0) return 0
  return Math.max(0, span - Math.max(0, day.break_minutes || 0))
}

// "1234.50" — the bare amount, no currency symbol. Every call site supplies
// its own `$` in the template, so adding one here would double it.
export function formatCurrency(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n.toFixed(2) : '0.00'
}

// "7h 30m" / "8h" / '' when zero. Floors first — a caller deriving minutes
// from raw clock-in/out timestamps (rather than whole-minute HH:mm inputs)
// can hand this a fraction, which `%` would otherwise pass straight through.
export function formatHours(minutes) {
  if (!minutes || minutes <= 0) return ''
  const total = Math.floor(minutes)
  const h = Math.floor(total / 60)
  const m = total % 60
  return m ? `${h}h ${m}m` : `${h}h`
}

// Net minutes between two clock-event timestamps, floored at zero.
//
// Clamped deliberately: approval used to persist rows whose clocked_out_at
// preceded its clocked_in_at, and those rows are still in the database. Left
// unclamped, one of them subtracts real hours from whatever company, project
// or pay-period total it lands in. Zero contains the damage to the one shift
// that is actually broken.
export function eventMinutes(clockedInIso, clockedOutIso, breakMinutes = 0) {
  if (!clockedInIso || !clockedOutIso) return 0
  const gross = (new Date(clockedOutIso) - new Date(clockedInIso)) / 60000
  if (!Number.isFinite(gross) || gross <= 0) return 0
  return Math.max(0, gross - (breakMinutes || 0))
}

// User-facing message for a day cell that would fail the API's own
// _validateRowDays() (ManagesSupervisorTimesheetRows.php), or null when the
// day is fine. Shared by SupervisorSubmitForm and SupervisorTimesheetTab so
// the two never drift apart the way they did before this was pulled out —
// both independently allowed a negative break_minutes client-side even
// though the API has always rejected it.
export function dayError(day) {
  if (!day || !day.present) return null
  // A half-entered day is fine — the API allows it too, and typing a start
  // before a finish is unavoidable. It simply computes as zero hours.
  if (!day.start || !day.finish) return null
  if (timeToMinutes(day.start) === null || timeToMinutes(day.finish) === null)
    return "That isn't a valid time."
  const span = spanMinutes(day.start, day.finish)
  if (span === 0) return "Start and finish can't be the same."
  if (span > MAX_SHIFT_MINUTES) {
    return `Longer than ${Math.floor(
      MAX_SHIFT_MINUTES / 60,
    )} hours — check the times.`
  }
  if ((day.break_minutes || 0) < 0) {
    return "Break can't be negative."
  }
  if ((day.break_minutes || 0) >= span) {
    return 'Break is longer than the shift.'
  }
  return null
}

// ── Leave types ────────────────────────────────────────────────────────────
//
// Supervisors classify what kind of day it was (leave_type); business admins
// price it (leave_hours). The supervisor endpoint deliberately carries no
// leave_hours key at all, so anything hours-related below is BA-only.
// Order matches the wire contract and the required dropdown order — Worked
// (the default, serialized as null) first.
export const LEAVE_TYPE_OPTIONS = [
  { value: null, text: 'Worked' },
  { value: 'public_holiday', text: 'Public Holiday' },
  { value: 'personal_leave', text: 'Personal Leave' },
  { value: 'sick_leave', text: 'Sick Leave' },
  { value: 'annual_leave', text: 'Annual Leave' },
]

const LEAVE_TYPE_LABELS = {
  public_holiday: 'Public Holiday',
  personal_leave: 'Personal Leave',
  sick_leave: 'Sick Leave',
  annual_leave: 'Annual Leave',
}

// null/undefined ("Worked") has no label — callers branch on isLeaveDay()
// first.
export function leaveTypeLabel(leaveType) {
  return LEAVE_TYPE_LABELS[leaveType] || null
}

export function isLeaveDay(day) {
  return !!(day && day.leave_type)
}

// A day the supervisor endpoint refuses to accept any change on — leave set
// by a business admin, or part of a multi-day leave block the day grid can't
// express. The API sends `leave_locked` on every day of both timesheet
// serializers; before it did, the grid had no way to know and a bulk "apply
// to entire week" would silently stomp such a day, only surfacing as a 422
// at submit time. Never infer this from leave_type — the supervisor's own
// single-day leave is leave, but it isn't locked.
export function isLockedLeaveDay(day) {
  return !!(day && day.leave_locked)
}

// Applies `patch` to `day` unless the day is locked, in which case the stored
// values are echoed back untouched. Every bulk mutator on the supervisor grid
// goes through this so none of them can reintroduce the stomp.
export function patchUnlockedDay(day, patch) {
  return isLockedLeaveDay(day) ? { ...day } : { ...day, ...patch }
}

// Decimal leave hours for the BA context, or null when the day isn't on
// leave or carries no value yet. A leave day's worked duration is always
// zero — dayMinutes() already returns 0 for it since present is cleared
// whenever leave_type is set.
export function dayLeaveHours(day) {
  if (!isLeaveDay(day)) return null
  if (day.leave_hours === null || day.leave_hours === undefined) return null
  const n = Number(day.leave_hours)
  return Number.isFinite(n) ? n : null
}

// User-facing message for an invalid leave-hours entry, mirroring the API's
// "Leave hours for {date} must be between 0 and 40." validation. Blank is
// valid client-side — the API resolves it to the company default on save.
// The cap allows a full week's leave (e.g. 40h) to be applied against a
// single day instead of spread across the week.
export function leaveHoursError(day) {
  if (!isLeaveDay(day)) return null
  if (
    day.leave_hours === null ||
    day.leave_hours === undefined ||
    day.leave_hours === ''
  )
    return null
  const n = Number(day.leave_hours)
  if (!Number.isFinite(n)) return 'Enter a number.'
  if (n < 0 || n > 40) return 'Leave hours must be between 0 and 40.'
  return null
}

// ── Supervisor edit-row transforms (pure) ───────────────────────────────────

/** Deep-clone helper used by edit-row transforms */
function cloneEditRows(rows) {
  return JSON.parse(JSON.stringify(rows))
}

export function withToggledWorker(rows, ri, value) {
  const next = cloneEditRows(rows)
  next[ri].on_site = value
  if (!value) {
    next[ri].days = next[ri].days.map((d) => ({
      ...d,
      present: false,
      start: null,
      finish: null,
      break_minutes: 0,
      leave_type: null,
      leave_hours: null,
    }))
  }
  return next
}

export function withSetPresent(rows, ri, di, value) {
  const next = cloneEditRows(rows)
  next[ri].days[di].present = value
  if (!value) {
    next[ri].days[di].start = null
    next[ri].days[di].finish = null
    next[ri].days[di].break_minutes = 0
  }
  return next
}

export function withSetDayField(rows, ri, di, field, value) {
  const next = cloneEditRows(rows)
  next[ri].days[di][field] = value
  return next
}

export function withSetLeaveType(rows, ri, di, value) {
  const next = cloneEditRows(rows)
  next[ri].days[di].leave_type = value || null
  if (value) {
    next[ri].days[di].present = false
    next[ri].days[di].start = null
    next[ri].days[di].finish = null
    next[ri].days[di].break_minutes = 0
  } else {
    next[ri].days[di].leave_hours = null
  }
  return next
}

export function withSetLeaveHours(rows, ri, di, value) {
  const next = cloneEditRows(rows)
  next[ri].days[di].leave_hours = value === '' ? null : Number(value)
  return next
}

export function buildEditRowsPayload(editRows) {
  return editRows.map((row) => ({
    worker: { id: row.worker.id },
    on_site: row.on_site,
    days: row.days.map((d) => ({
      date: d.date,
      start: d.start || null,
      finish: d.finish || null,
      break_minutes: d.break_minutes || 0,
      present: d.present,
      leave_type: d.leave_type || null,
      leave_hours: d.leave_type
        ? d.leave_hours === null || d.leave_hours === undefined
          ? null
          : Number(d.leave_hours)
        : null,
    })),
  }))
}

// Sheet-level crew plant is a full-replace payload (see
// SupervisorTimesheetController::_saveSheetPlant on the API side): every
// date's current entries must be resent together, since an omitted date
// clears that date. Reads straight off the live ts.plant_entries tree, which
// already reflects local adds/removes made before save.
export function buildPlantEntriesPayload(plantEntries) {
  return (plantEntries || []).map((e) => ({
    date: e.date,
    plant_equipment_id: e.plant_equipment_id,
    quantity: e.quantity,
  }))
}

// `zoneEvents` can span more than one project (e.g. the supervisor
// timesheet table's cross-project geofence check), so there is no single
// "right" tz for the whole set — each event buckets by its OWN stamped
// `timezone` (the project's resolved timezone at clock-in, set by the
// API's saving hook), matching how the day it's compared against was built
// (see docs/designs/per-site-timezones.md, "supervisor grid is one
// timezone end to end"). `tz` is only the fallback for an event that
// predates the timezone column.
export function buildZoneVerifiedDayKeys(zoneEvents, tz = BUSINESS_TZ) {
  const keys = new Set()
  ;(zoneEvents || []).forEach((event) => {
    if (!wasZoneVerified(event)) return
    const workerId = event.tradie && event.tradie.id
    if (workerId == null || !event.clocked_in_at) return
    const date = bizFromIso(event.clocked_in_at, event.timezone || tz).format(
      'YYYY-MM-DD',
    )
    keys.add(`${workerId}|${date}`)
  })
  return keys
}

export function rowZoneStatus(row, zoneVerifiedDayKeys) {
  const workedDays = (row.days || []).filter((d) => d.present)
  if (!workedDays.length) return 'not-worked'
  const allVerified = workedDays.every((d) =>
    zoneVerifiedDayKeys.has(`${row.worker.id}|${d.date}`),
  )
  return allVerified ? 'verified' : 'unverified'
}

export function dayZoneStatus(row, day, zoneVerifiedDayKeys) {
  if (!day || !day.present) return 'not-worked'
  return zoneVerifiedDayKeys.has(`${row.worker.id}|${day.date}`)
    ? 'verified'
    : 'unverified'
}

export function zoneStatusVariant(status) {
  return { verified: 'success', unverified: 'warning' }[status] || 'secondary'
}

export function zoneStatusLabel(status) {
  return { verified: 'Verified', unverified: 'Unverified' }[status] || '-'
}

export function dayHoursDecimal(day) {
  const mins = dayMinutes(day)
  return mins > 0 ? (mins / 60).toFixed(2) : ''
}

export function leaveHoursExport(day) {
  const h = dayLeaveHours(day)
  return h != null ? h.toFixed(2) : ''
}

/**
 * Build CSV/PDF data rows (including header as first row) for the tradie
 * timesheet-list export. events: array of timesheet events (already
 * filtered/sorted). columns: { [colKey]: boolean } selection from the
 * export modal.
 */
export function buildTimesheetExportRows(events, columns, tz = BUSINESS_TZ) {
  // Export can span multiple projects at once (CrewHistoryTab/TimesheetListTab
  // pass mixed-project event lists), so a single passed `tz` would misrender
  // every row not from the "expected" project — same bug class fixed in
  // buildZoneVerifiedDayKeys (2.3). Each event's own stamped timezone wins;
  // `tz` is only the fallback for an event that predates it.
  const rowTz = (e) => e.timezone || tz
  const colDefs = [
    {
      key: 'date',
      label: 'Date',
      val: (e) => {
        if (e.is_leave)
          return e.leave_date ? dayjs(e.leave_date).format('DD/MM/YYYY') : ''
        return e.clocked_in_at
          ? bizFromIso(e.clocked_in_at, rowTz(e)).format('DD/MM/YYYY')
          : ''
      },
    },
    {
      key: 'tradieName',
      label: 'Tradie Name',
      val: (e) => (e.tradie && e.tradie.name) || '',
    },
    {
      key: 'project',
      label: 'Project',
      val: (e) => (e.project && e.project.name) || '',
    },
    {
      key: 'zone',
      label: 'Zone',
      val: (e) => (e.is_leave ? '' : (e.zone && e.zone.name) || ''),
    },
    {
      key: 'startTime',
      label: 'Start Time',
      val: (e) =>
        !e.is_leave && e.clocked_in_at
          ? formatBusinessTime(e.clocked_in_at, 'h:mm a', rowTz(e))
          : '',
    },
    {
      key: 'endTime',
      label: 'End Time',
      val: (e) =>
        !e.is_leave && e.clocked_out_at
          ? formatBusinessTime(e.clocked_out_at, 'h:mm a', rowTz(e))
          : '',
    },
    {
      key: 'hours',
      label: 'Hours',
      val: (e) => {
        if (e.is_leave) {
          const h =
            e.leave_resolved_hours != null
              ? e.leave_resolved_hours
              : e.leave_hours_per_day
          return h != null ? Number(h).toFixed(2) : ''
        }
        return e.clocked_in_at && e.clocked_out_at
          ? (
              ((new Date(e.clocked_out_at) - new Date(e.clocked_in_at)) /
                60000 -
                (e.break_minutes || 0)) /
              60
            ).toFixed(2)
          : ''
      },
    },
    {
      key: 'breakMinutes',
      label: 'Break (mins)',
      val: (e) => (e.is_leave ? 0 : e.break_minutes || 0),
    },
    {
      key: 'status',
      label: 'Status',
      val: (e) =>
        e.is_leave
          ? leaveTypeLabel(e.leave_type) || 'Leave'
          : eventStatusLabel(e),
    },
    {
      key: 'comment',
      label: 'Work Comments',
      val: (e) => (e.is_leave ? e.leave_note || '' : e.comment || ''),
    },
    {
      key: 'shift',
      label: 'Day / Night',
      val: (e) => {
        if (e.is_leave || !e.clocked_in_at) return ''
        return isNightClockIn(e.clocked_in_at, rowTz(e)) ? 'Night' : 'Day'
      },
    },
  ]

  const rows = [colDefs.filter((c) => columns[c.key]).map((c) => c.label)]
  events.forEach((e) => {
    rows.push(
      colDefs.filter((c) => columns[c.key]).map((c) => String(c.val(e))),
    )
  })
  return rows
}

/**
 * Build CSV/PDF data rows (including header as first row).
 * sheets: array of timesheet objects (already filtered)
 * workerFilter: number[] (empty = all)
 * zoneVerifiedDayKeys: Set
 */
export function buildSupervisorExportRows(
  sheets,
  { workerFilter = [], zoneVerifiedDayKeys = new Set() } = {},
) {
  const fmtDate = (d) =>
    new Date(d + 'T00:00:00').toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1)

  const header = [
    'Project',
    'Week Start',
    'Supervisor',
    'Employee',
    'Trade',
    'Date',
    'Day',
    'Start',
    'Finish',
    'Type',
    'Hours',
    'On Site',
    'Status',
    'Day / Night',
  ]

  const rows = [header]
  ;(sheets || []).forEach((ts) => {
    const status = capitalize(ts.status)
    ts.rows.forEach((row) => {
      if (workerFilter.length && !workerFilter.includes(row.worker.id)) return
      row.days.forEach((day) => {
        const leave = isLeaveDay(day)
        // The export is a record of time actually worked. Emit a row only
        // for a leave day or a day with measurable clocked hours — skip
        // absent, zero-hour and still-scheduled days so the report doesn't
        // list an "Onsite" line (with an On Site status) for someone who
        // never clocked on. Mirrors the server-side
        // SupervisorTimesheetReportRows::build().
        if (!leave && dayMinutes(day) <= 0) return
        rows.push([
          ts.project_name,
          fmtDate(ts.week_start),
          ts.supervisor.full_name,
          row.worker.full_name,
          formatTrade(row.worker.trade) || '',
          fmtDate(day.date),
          new Date(day.date + 'T00:00:00').toLocaleDateString('en-AU', {
            weekday: 'long',
          }),
          leave ? '' : day.start || '',
          leave ? '' : day.finish || '',
          leave ? leaveTypeLabel(day.leave_type) : 'Onsite',
          leave ? leaveHoursExport(day) : dayHoursDecimal(day),
          zoneStatusLabel(dayZoneStatus(row, day, zoneVerifiedDayKeys)),
          status,
          leave || !day.start
            ? ''
            : (
                typeof day.is_night === 'boolean'
                  ? day.is_night
                  : isNightStart(day.start, day.finish)
              )
            ? 'Night'
            : 'Day',
        ])
      })
    })
  })

  return rows
}

export function plantUnitLabel(rateType) {
  return { hourly: 'Hr', day: 'Day' }[rateType] || ''
}
